/**
 * Knowledge Base / Help Center with Visual Guides
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Comprehensive searchable knowledge base for ALL user types
 * with step-by-step visual illustrations
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Search, Home, Palmtree, Building2, Users, CreditCard, 
  HelpCircle, BookOpen, ArrowLeft, ChevronRight, Mail,
  Key, Settings, DollarSign, Calendar, Link2, BarChart3,
  FileText, MessageCircle, Wrench, UserCheck,
  ClipboardList, Bell, CheckCircle2, Circle, ArrowRight,
  MousePointer, Monitor, Smartphone, Code, Copy, Globe,
  CheckCircle, Eye, Palette
} from 'lucide-react';

// Visual Step Component for guided instructions
const VisualStep = ({ number, title, description, icon: Icon, highlight }) => (
  <div className={`flex gap-4 p-4 rounded-lg ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'}`}>
    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${highlight ? 'bg-emerald-600' : 'bg-gray-500'}`}>
      {number}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`h-5 w-5 ${highlight ? 'text-emerald-600' : 'text-gray-500'}`} />}
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </div>
);

// Screenshot placeholder with description
const ScreenshotGuide = ({ title, description, steps, loginUrl }) => (
  <div className="my-6 border rounded-lg overflow-hidden">
    <div className="bg-gray-800 text-white px-4 py-2 flex items-center gap-2">
      <Monitor className="h-4 w-4" />
      <span className="text-sm font-medium">{title}</span>
    </div>
    <div className="p-4 bg-gradient-to-br from-gray-100 to-gray-200">
      <div className="bg-white rounded-lg shadow-lg p-6 min-h-[200px]">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <div className="flex gap-1">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
          </div>
          {loginUrl && (
            <div className="flex-1 bg-gray-100 rounded px-3 py-1 text-xs text-gray-600 font-mono">
              {loginUrl}
            </div>
          )}
        </div>
        <p className="text-gray-600 text-center mb-4">{description}</p>
        {steps && (
          <div className="space-y-2">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

// Interactive Button Guide
const ButtonGuide = ({ buttons }) => (
  <div className="my-4 p-4 bg-gray-50 rounded-lg">
    <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
      <MousePointer className="h-4 w-4" />
      Look for these buttons:
    </p>
    <div className="flex flex-wrap gap-2">
      {buttons.map((btn, i) => (
        <div 
          key={i} 
          className={`px-4 py-2 rounded-md text-sm font-medium ${btn.variant === 'primary' ? 'bg-emerald-600 text-white' : btn.variant === 'outline' ? 'border border-emerald-600 text-emerald-600 bg-white' : 'bg-gray-200 text-gray-700'}`}
        >
          {btn.label}
        </div>
      ))}
    </div>
  </div>
);

// URL Path Guide
const PathGuide = ({ path, description }) => (
  <div className="my-3 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
    <div className="flex-shrink-0 p-2 bg-blue-100 rounded">
      <Link2 className="h-5 w-5 text-blue-600" />
    </div>
    <div>
      <code className="text-blue-800 font-mono font-bold">{path}</code>
      {description && <p className="text-blue-600 text-sm mt-1">{description}</p>}
    </div>
  </div>
);

// ============================================
// KNOWLEDGE BASE ARTICLES WITH VISUAL GUIDES
// ============================================
const ARTICLES = [
  // ==========================================
  // VACATION RENTAL MANAGER ARTICLES
  // ==========================================
  {
    id: 'vr-getting-started',
    category: 'vacation-rental',
    title: 'Getting Started as a Vacation Rental Manager',
    description: 'Complete visual guide to signing up and setting up your vacation rental business',
    icon: Palmtree,
    tags: ['getting started', 'signup', 'onboarding', 'new user', 'vacation rental'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Getting Started as a Vacation Rental Manager</h1>
        <p className="text-gray-600">Welcome to Estate Flow! Follow this visual guide to set up your vacation rental management account.</p>
        
        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Access the Signup Page</h2>
        <PathGuide path="/vacation-rentals/search" description="Go to the vacation rentals search page" />
        <ButtonGuide buttons={[
          { label: 'List Your Property', variant: 'outline' },
          { label: 'Get Started - $99 Setup', variant: 'primary' }
        ]} />
        <ScreenshotGuide 
          title="Vacation Rentals Page Header"
          loginUrl="apcestateflow.com/vacation-rentals/search"
          description="Click 'List Your Property' button or the orange banner"
          steps={[
            "Find the green 'List Your Property' button in the header",
            "Or click the orange banner 'Get Started - $99 Setup'",
            "Both will take you to the signup page"
          ]}
        />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Complete Signup Form</h2>
        <PathGuide path="/signup?role=vacation_rental" description="Signup page with Vacation Rental Manager selected" />
        <div className="space-y-3">
          <VisualStep number={1} title="Enter Your Name" description="First name and last name" icon={UserCheck} />
          <VisualStep number={2} title="Enter Email" description="Use a valid email - this will be your login" icon={Mail} highlight />
          <VisualStep number={3} title="Create Password" description="Minimum 8 characters, include numbers and letters" icon={Key} />
          <VisualStep number={4} title="Phone Number" description="For account recovery and notifications" icon={Smartphone} />
          <VisualStep number={5} title="Select Country" description="Choose your country from dropdown" icon={Home} />
        </div>
        <p className="text-sm text-gray-500 mt-2">Note: "Vacation Rental Manager" should be pre-selected in the role dropdown.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 3: Complete Onboarding</h2>
        <p className="text-gray-600">After signup, you'll go through our onboarding wizard:</p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Card className="p-4">
            <h4 className="font-semibold flex items-center gap-2"><Building2 className="h-5 w-5 text-emerald-600" /> Organization Details</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Business/Organization Name</li>
              <li>• Business Address</li>
              <li>• Contact Information</li>
            </ul>
          </Card>
          <Card className="p-4">
            <h4 className="font-semibold flex items-center gap-2"><Home className="h-5 w-5 text-emerald-600" /> Property Information</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Number of properties</li>
              <li>• Average units per property</li>
            </ul>
          </Card>
          <Card className="p-4">
            <h4 className="font-semibold flex items-center gap-2"><CreditCard className="h-5 w-5 text-emerald-600" /> Payment Setup</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Bank account for payouts</li>
              <li>• Payout method preference</li>
            </ul>
          </Card>
          <Card className="p-4 border-emerald-200 bg-emerald-50">
            <h4 className="font-semibold flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-600" /> Review & Pay</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Setup Fee: <strong>$99</strong></li>
              <li>• Monthly: Based on properties</li>
              <li>• Secure Stripe checkout</li>
            </ul>
          </Card>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 4: Access Your Dashboard</h2>
        <PathGuide path="/vacation-rental/login" description="Your login page for future access" />
        <ScreenshotGuide 
          title="Your Vacation Rentals Dashboard"
          loginUrl="apcestateflow.com/vacation-rentals"
          description="Your management dashboard after login"
          steps={[
            "Overview - See key metrics at a glance",
            "Properties - Add and manage your rentals",
            "Bookings - View and manage reservations",
            "Channel Manager - Connect to Airbnb, Expedia, etc.",
            "Payouts - Track and request earnings"
          ]}
        />
      </div>
    )
  },
  {
    id: 'vr-add-property',
    category: 'vacation-rental',
    title: 'How to Add a Vacation Rental Property',
    description: 'Step-by-step visual guide to adding and configuring your rental properties',
    icon: Home,
    tags: ['property', 'add', 'create', 'listing', 'setup', 'vacation rental'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">How to Add a Vacation Rental Property</h1>
        <p className="text-gray-600">Follow this visual guide to add your first property.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Go to Properties Tab</h2>
        <PathGuide path="/vacation-rentals" description="Login to your dashboard first" />
        <ScreenshotGuide 
          title="Dashboard Navigation"
          loginUrl="apcestateflow.com/vacation-rentals"
          description="Find the Properties tab in your dashboard"
          steps={[
            "Login at /vacation-rental/login",
            "Look at the tabs: Overview, Properties, Bookings...",
            "Click on 'Properties' tab"
          ]}
        />
        <ButtonGuide buttons={[
          { label: 'Properties', variant: 'default' }
        ]} />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Click Add Property</h2>
        <ButtonGuide buttons={[
          { label: '+ Add Property', variant: 'primary' }
        ]} />
        <p className="text-gray-600">Look for the green "Add Property" button in the top right of the Properties section.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 3: Fill in Property Details</h2>
        <div className="space-y-4">
          <Card className="p-4 border-l-4 border-l-emerald-500">
            <h4 className="font-semibold">Basic Information</h4>
            <div className="mt-2 space-y-2">
              <VisualStep number={1} title="Property Name" description="Give it a catchy name like 'Beachfront Paradise Villa'" icon={Home} highlight />
              <VisualStep number={2} title="Property Type" description="Select: Villa, Condo, Apartment, House, Cottage, or Beachfront" icon={Building2} />
              <VisualStep number={3} title="Description" description="Write an appealing description highlighting key features" icon={FileText} />
            </div>
          </Card>

          <Card className="p-4 border-l-4 border-l-blue-500">
            <h4 className="font-semibold">Location</h4>
            <div className="mt-2 space-y-2">
              <VisualStep number={4} title="City" description="Enter the city name" icon={Home} />
              <VisualStep number={5} title="Country" description="Select from the dropdown" icon={Home} />
              <VisualStep number={6} title="Address" description="Full street address for guests" icon={Home} />
            </div>
          </Card>

          <Card className="p-4 border-l-4 border-l-purple-500">
            <h4 className="font-semibold">Property Details</h4>
            <div className="mt-2 grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-purple-600">🛏️</div>
                <div className="text-sm">Bedrooms</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-purple-600">🚿</div>
                <div className="text-sm">Bathrooms</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-purple-600">👥</div>
                <div className="text-sm">Max Guests</div>
              </div>
            </div>
          </Card>

          <Card className="p-4 border-l-4 border-l-green-500">
            <h4 className="font-semibold">Pricing</h4>
            <div className="mt-2 space-y-2">
              <VisualStep number={7} title="Base Nightly Rate" description="Your standard per-night price (e.g., $150)" icon={DollarSign} highlight />
              <VisualStep number={8} title="Cleaning Fee" description="One-time fee per stay (e.g., $75)" icon={DollarSign} />
              <VisualStep number={9} title="Direct Booking Discount" description="Percentage off for direct bookings (recommended: 10-15%)" icon={DollarSign} />
            </div>
          </Card>

          <Card className="p-4 border-l-4 border-l-amber-500">
            <h4 className="font-semibold">Amenities (Select All That Apply)</h4>
            <div className="mt-2 flex flex-wrap gap-2">
              {['WiFi', 'Parking', 'Kitchen', 'Pool', 'Beach Access', 'Air Conditioning', 'TV', 'Washer/Dryer'].map(a => (
                <span key={a} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">{a}</span>
              ))}
            </div>
          </Card>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 4: Save and Publish</h2>
        <ButtonGuide buttons={[
          { label: 'Add Property', variant: 'primary' }
        ]} />
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mt-4">
          <h4 className="font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Make Property Public
          </h4>
          <p className="text-sm text-gray-600 mt-1">After adding, toggle <strong>"List Publicly"</strong> to show your property on the public search page at /vacation-rentals/search</p>
        </div>
      </div>
    )
  },
  {
    id: 'vr-bookings',
    category: 'vacation-rental',
    title: 'Managing Vacation Rental Bookings',
    description: 'Visual guide to viewing, confirming, and managing guest reservations',
    icon: Calendar,
    tags: ['bookings', 'reservations', 'guests', 'calendar', 'vacation rental'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Managing Bookings</h1>
        <p className="text-gray-600">Learn how to manage your vacation rental bookings effectively.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Viewing Your Bookings</h2>
        <PathGuide path="/vacation-rentals" description="Go to Bookings tab in your dashboard" />
        <ButtonGuide buttons={[{ label: 'Bookings', variant: 'default' }]} />

        <ScreenshotGuide 
          title="Bookings Dashboard"
          loginUrl="apcestateflow.com/vacation-rentals"
          description="Your bookings are organized by status"
          steps={[
            "Upcoming - Future reservations",
            "Current - Guests currently staying",
            "Past - Completed stays",
            "Cancelled - Cancelled reservations"
          ]}
        />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Booking Information</h2>
        <Card className="p-4">
          <p className="text-sm text-gray-500 mb-3">Each booking shows:</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-emerald-600" /> Guest name & contact</div>
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-emerald-600" /> Check-in/out dates</div>
            <div className="flex items-center gap-2"><Users className="h-4 w-4 text-emerald-600" /> Number of guests</div>
            <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-600" /> Total amount</div>
            <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-emerald-600" /> Payment status</div>
            <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-emerald-600" /> Booking source</div>
          </div>
        </Card>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Confirming Bookings</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Review Booking" description="New bookings appear as 'Pending'" icon={FileText} />
          <VisualStep number={2} title="Check Guest Info" description="Verify guest details and dates" icon={UserCheck} />
          <VisualStep number={3} title="Confirm" description="Click 'Confirm' to accept the booking" icon={CheckCircle2} highlight />
        </div>
        <ButtonGuide buttons={[
          { label: 'Confirm', variant: 'primary' },
          { label: 'Decline', variant: 'outline' }
        ]} />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Booking Sources</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          {[
            { name: 'Book Direct', color: 'bg-emerald-100 text-emerald-800', best: true },
            { name: 'Expedia', color: 'bg-yellow-100 text-yellow-800' },
            { name: 'Booking.com', color: 'bg-blue-100 text-blue-800' },
            { name: 'VRBO', color: 'bg-purple-100 text-purple-800' },
            { name: 'Airbnb', color: 'bg-red-100 text-red-800' }
          ].map(s => (
            <div key={s.name} className={`p-3 rounded-lg text-center ${s.color}`}>
              <div className="font-medium text-sm">{s.name}</div>
              {s.best && <div className="text-xs mt-1">Lowest fees!</div>}
            </div>
          ))}
        </div>
      </div>
    )
  },

  // WEBSITE WIDGET ARTICLE
  {
    id: 'vr-website-widget',
    category: 'vacation-rental',
    title: 'Website Booking Widget - Embed on Your Website',
    description: 'How to add a booking widget to your own website so guests can book directly',
    icon: Code,
    tags: ['widget', 'embed', 'website', 'booking', 'iframe', 'code', 'integration', 'vacation rental', 'book direct'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Website Booking Widget</h1>
        <p className="text-gray-600">Add a fully functional booking widget to your own website. Guests can search, browse, and book all your properties directly - without ever leaving your site!</p>
        
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Why Use the Widget?
          </h3>
          <ul className="mt-2 text-emerald-700 space-y-1">
            <li>• Guests save <strong>10-15%</strong> vs booking on Airbnb/VRBO</li>
            <li>• <strong>No redirect</strong> - entire booking happens on YOUR website</li>
            <li>• <strong>Customizable colors</strong> to match your branding</li>
            <li>• <strong>Mobile responsive</strong> - works on all devices</li>
            <li>• <strong>Secure checkout</strong> - PCI compliant payments</li>
          </ul>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Access Widget Settings</h2>
        <PathGuide path="/vacation-rentals" description="Go to your Vacation Rental Dashboard" />
        <div className="space-y-3">
          <VisualStep number={1} title="Login to Dashboard" description="Go to apcestateflow.com/vacation-rental/login" icon={ArrowRight} />
          <VisualStep number={2} title="Click 'Book Direct Widget' Tab" description="Find it in the navigation tabs at the top" icon={Code} highlight />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Customize Your Widget</h2>
        <p className="text-gray-600">Match the widget colors to your website branding:</p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Card className="p-4">
            <h4 className="font-semibold flex items-center gap-2"><Palette className="h-5 w-5 text-emerald-600" /> Primary Color</h4>
            <p className="text-sm text-gray-600 mt-1">Main buttons and headers. Click the color picker to choose.</p>
          </Card>
          <Card className="p-4">
            <h4 className="font-semibold flex items-center gap-2"><Palette className="h-5 w-5 text-blue-600" /> Accent Color</h4>
            <p className="text-sm text-gray-600 mt-1">Highlights and secondary elements.</p>
          </Card>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 3: Copy the Embed Code</h2>
        <p className="text-gray-600">You'll see two options:</p>
        
        <div className="space-y-4 mt-4">
          <Card className="p-4 border-emerald-200">
            <h4 className="font-semibold text-emerald-700">Option A: All Properties Widget (Recommended)</h4>
            <p className="text-sm text-gray-600 mt-1">Shows all your properties with search and filter. Best for a dedicated "Book Now" page.</p>
            <div className="bg-gray-900 text-gray-100 rounded-lg p-4 mt-3 font-mono text-sm overflow-x-auto">
              <code>{`<iframe
  src="https://apcestateflow.com/embed/booking/[YOUR-ID]"
  width="100%"
  height="800"
  style="border: none;"
  allow="payment"
></iframe>`}</code>
            </div>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold">Option B: Single Property Widget</h4>
            <p className="text-sm text-gray-600 mt-1">Embed a widget for one specific property. Good for individual property pages.</p>
          </Card>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 4: Add to Your Website</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Copy the Code" description="Click the 'Copy Embed Code' button in your dashboard" icon={Copy} />
          <VisualStep number={2} title="Edit Your Website" description="Go to your website builder (Wix, Squarespace, WordPress, etc.)" icon={Globe} />
          <VisualStep number={3} title="Add HTML Block" description="Add an 'HTML' or 'Embed' block on your booking page" icon={Code} highlight />
          <VisualStep number={4} title="Paste & Save" description="Paste the iframe code and publish your changes" icon={CheckCircle} />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <h3 className="font-semibold text-blue-800 flex items-center gap-2">
            <Eye className="h-5 w-5" /> Preview Your Widget
          </h3>
          <p className="text-blue-700 mt-1">
            Before embedding, click <strong>"Preview Widget"</strong> in your dashboard to see exactly how it will look on your website.
          </p>
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Widget Features</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Search className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold">Search & Filter</h4>
              <p className="text-sm text-gray-600">Guests can filter by dates, guests, bedrooms</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold">Live Availability</h4>
              <p className="text-sm text-gray-600">Calendar shows real-time availability</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CreditCard className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold">Secure Checkout</h4>
              <p className="text-sm text-gray-600">Complete payment within the widget</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold">Mobile Responsive</h4>
              <p className="text-sm text-gray-600">Works perfectly on phones and tablets</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
          <h3 className="font-semibold text-yellow-800">💡 Pro Tip</h3>
          <p className="text-yellow-700 mt-1">
            Create a dedicated "Book Direct" page on your website and promote it on social media. 
            Remind guests they save 10-15% by booking directly with you instead of through Airbnb or VRBO!
          </p>
        </div>
      </div>
    )
  },

  // ==========================================
  // LANDLORD ARTICLES
  // ==========================================
  {
    id: 'landlord-getting-started',
    category: 'landlord',
    title: 'Getting Started as a Landlord',
    description: 'Visual guide to setting up your landlord account',
    icon: Building2,
    tags: ['landlord', 'getting started', 'property management', 'rent', 'new user'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Getting Started as a Landlord</h1>
        <p className="text-gray-600">Welcome! Follow this visual guide to set up your landlord account.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Sign Up</h2>
        <PathGuide path="/signup" description="Go to the signup page" />
        <div className="space-y-3">
          <VisualStep number={1} title="Go to Signup" description="Visit /signup" icon={ArrowRight} />
          <VisualStep number={2} title="Select 'Landlord'" description="Choose Landlord from the role dropdown" icon={Building2} highlight />
          <VisualStep number={3} title="Complete Form" description="Fill in your name, email, password" icon={FileText} />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Login to Dashboard</h2>
        <PathGuide path="/landlord/login" description="Your landlord login page" />
        <ScreenshotGuide 
          title="Landlord Dashboard"
          loginUrl="apcestateflow.com/landlord/dashboard"
          description="Your property management dashboard"
          steps={[
            "Properties - Add and manage rental properties",
            "Tenants - Invite and manage tenants",
            "Payments - Track rent collection",
            "Maintenance - Handle repair requests",
            "Reports - Financial reporting"
          ]}
        />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Pricing</h2>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-emerald-600">$162</div>
              <div className="text-sm text-gray-600">Setup Fee (base)</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">$161-$755</div>
              <div className="text-sm text-gray-600">Monthly (by units)</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">+$54/mo</div>
              <div className="text-sm text-gray-600">Per additional building</div>
            </div>
          </div>
        </Card>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">First Steps After Setup</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Add Properties" description="Click 'Add Property' to add your rental properties" icon={Home} />
          <VisualStep number={2} title="Add Units" description="Add apartments/units to each property" icon={Building2} />
          <VisualStep number={3} title="Set Rent" description="Configure rent amount for each unit" icon={DollarSign} />
          <VisualStep number={4} title="Invite Tenants" description="Send invitations to your tenants" icon={Users} highlight />
          <VisualStep number={5} title="Collect Rent" description="Tenants pay online, you receive automatically" icon={CreditCard} />
        </div>
      </div>
    )
  },
  {
    id: 'landlord-add-tenant',
    category: 'landlord',
    title: 'How to Add and Invite Tenants',
    description: 'Visual guide to inviting tenants to your properties',
    icon: Users,
    tags: ['landlord', 'tenant', 'add', 'invite', 'renter', 'lease'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">How to Add and Invite Tenants</h1>
        <p className="text-gray-600">Follow this visual guide to invite tenants to your rental properties.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step-by-Step Process</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Login to Dashboard" description="Go to /landlord/login" icon={Key} />
          <VisualStep number={2} title="Go to Properties" description="Click on the Properties section" icon={Building2} />
          <VisualStep number={3} title="Select Property" description="Click on the property you want to add a tenant to" icon={Home} />
          <VisualStep number={4} title="Select Unit" description="Click on the specific unit" icon={Home} />
          <VisualStep number={5} title="Click Add Tenant" description="Find the 'Add Tenant' or 'Invite Tenant' button" icon={Users} highlight />
        </div>

        <ButtonGuide buttons={[
          { label: 'Add Tenant', variant: 'primary' },
          { label: 'Invite Tenant', variant: 'outline' }
        ]} />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Tenant Information to Enter</h2>
        <Card className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
              <Mail className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-medium">Email Address</div>
                <div className="text-sm text-gray-500">Tenant will receive invitation here</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
              <Calendar className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-medium">Lease Start & End Date</div>
                <div className="text-sm text-gray-500">Duration of the lease</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-emerald-50 rounded border border-emerald-200">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-medium">Monthly Rent Amount</div>
                <div className="text-sm text-gray-500">How much rent they pay each month</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-medium">Security Deposit</div>
                <div className="text-sm text-gray-500">Optional deposit amount</div>
              </div>
            </div>
          </div>
        </Card>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">What Happens Next?</h2>
        <ScreenshotGuide 
          title="Tenant Receives Email"
          description="After you send the invitation, the tenant receives an email"
          steps={[
            "Tenant clicks 'Accept Invitation' in email",
            "They create their account",
            "They set up payment method",
            "They can now pay rent online!"
          ]}
        />
      </div>
    )
  },

  // ==========================================
  // TENANT ARTICLES
  // ==========================================
  {
    id: 'tenant-getting-started',
    category: 'tenant',
    title: 'Getting Started as a Tenant',
    description: 'Visual guide to accessing your tenant portal',
    icon: UserCheck,
    tags: ['tenant', 'getting started', 'new user', 'renter'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Getting Started as a Tenant</h1>
        <p className="text-gray-600">Welcome! Your landlord has invited you to use Estate Flow.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Accept Your Invitation</h2>
        <ScreenshotGuide 
          title="Check Your Email"
          description="Look for an email invitation from your landlord"
          steps={[
            "Open the email from Estate Flow",
            "Click 'Accept Invitation'",
            "Create your account"
          ]}
        />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Create Your Account</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Confirm Email" description="Your email will be pre-filled" icon={Mail} />
          <VisualStep number={2} title="Create Password" description="Choose a secure password" icon={Key} highlight />
          <VisualStep number={3} title="Add Phone" description="For notifications and recovery" icon={Smartphone} />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 3: Access Your Portal</h2>
        <PathGuide path="/tenant/login" description="Your tenant login page" />
        <ScreenshotGuide 
          title="Tenant Dashboard"
          loginUrl="apcestateflow.com/tenant/dashboard"
          description="Your tenant portal features"
          steps={[
            "Pay Rent - Make monthly payments online",
            "Payment History - View all past payments",
            "Maintenance - Submit repair requests",
            "Documents - Access lease documents",
            "Messages - Chat with landlord"
          ]}
        />
      </div>
    )
  },
  {
    id: 'tenant-pay-rent',
    category: 'tenant',
    title: 'How to Pay Rent Online',
    description: 'Visual step-by-step guide to making rent payments',
    icon: CreditCard,
    tags: ['tenant', 'rent', 'payment', 'pay', 'online'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">How to Pay Rent Online</h1>
        <p className="text-gray-600">Follow this guide to pay your rent through the tenant portal.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 1: Login</h2>
        <PathGuide path="/tenant/login" description="Login to your tenant portal" />

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 2: Set Up Payment Method (First Time Only)</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Go to Payment Methods" description="Find in your dashboard settings" icon={Settings} />
          <VisualStep number={2} title="Add Payment Method" description="Click 'Add Payment Method'" icon={CreditCard} highlight />
          <VisualStep number={3} title="Choose Type" description="Credit Card, Debit Card, or Bank Account" icon={CreditCard} />
          <VisualStep number={4} title="Enter Details" description="Card number or bank details" icon={Key} />
          <VisualStep number={5} title="Save" description="Your method is saved securely" icon={CheckCircle2} />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Step 3: Make a Payment</h2>
        <ButtonGuide buttons={[{ label: 'Pay Rent', variant: 'primary' }]} />
        <div className="space-y-3 mt-4">
          <VisualStep number={1} title="Click Pay Rent" description="Big button on your dashboard" icon={CreditCard} highlight />
          <VisualStep number={2} title="Confirm Amount" description="Review the rent amount" icon={DollarSign} />
          <VisualStep number={3} title="Select Payment Method" description="Choose saved card or bank" icon={CreditCard} />
          <VisualStep number={4} title="Submit Payment" description="Click to confirm" icon={CheckCircle2} />
          <VisualStep number={5} title="Get Confirmation" description="Receipt sent to your email" icon={Mail} />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Optional: Set Up Auto-Pay</h2>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <h4 className="font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Never Miss a Payment!
          </h4>
          <p className="text-sm text-gray-600 mt-2">Enable Auto-Pay in Payment Settings to have rent automatically paid on the due date each month.</p>
        </Card>
      </div>
    )
  },

  // ==========================================
  // LOGIN HELP
  // ==========================================
  {
    id: 'login-help',
    category: 'account',
    title: 'Login Help & Troubleshooting',
    description: 'Visual guide to finding your login page',
    icon: Key,
    tags: ['login', 'password', 'access', 'sign in', 'forgot password', 'help'],
    hasVisuals: true,
    renderContent: () => (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Login Help</h1>
        <p className="text-gray-600">Find your login page and resolve common access issues.</p>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Login Pages by Account Type</h2>
        <div className="space-y-3">
          <PathGuide path="/vacation-rental/login" description="Vacation Rental Managers" />
          <PathGuide path="/landlord/login" description="Landlords" />
          <PathGuide path="/tenant/login" description="Tenants" />
          <PathGuide path="/property-manager/login" description="Property Managers" />
          <PathGuide path="/hoa-admin/login" description="HOA Administrators" />
          <PathGuide path="/hoa-owner/login" description="HOA Owners" />
          <PathGuide path="/guest/login" description="Guests (Vacation Booking)" />
          <PathGuide path="/referral/login" description="Referral Partners" />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Forgot Password?</h2>
        <div className="space-y-3">
          <VisualStep number={1} title="Go to Login Page" description="Visit your account type's login page" icon={Key} />
          <VisualStep number={2} title="Click 'Forgot Password?'" description="Find the link below the password field" icon={HelpCircle} highlight />
          <VisualStep number={3} title="Enter Email" description="Use the email you signed up with" icon={Mail} />
          <VisualStep number={4} title="Check Inbox" description="Look for reset email (check spam too!)" icon={Mail} />
          <VisualStep number={5} title="Reset Password" description="Click link and create new password" icon={Key} />
        </div>

        <h2 className="text-xl font-semibold text-emerald-700 mt-8">Common Issues</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-4">
            <h4 className="font-semibold text-red-600">❌ Invalid email or password</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Check email spelling</li>
              <li>• Ensure caps lock is off</li>
              <li>• Try resetting password</li>
            </ul>
          </Card>
          <Card className="p-4">
            <h4 className="font-semibold text-amber-600">⚠️ Account not found</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Verify correct login page</li>
              <li>• You may need to sign up first</li>
              <li>• Contact support@apcestateflow.com</li>
            </ul>
          </Card>
        </div>
      </div>
    )
  }
];

// Add remaining articles without visuals (text-only)
const TEXT_ARTICLES = [
  {
    id: 'vr-channel-manager',
    category: 'vacation-rental',
    title: 'Channel Manager - Connect to Airbnb & More',
    description: 'Sync your properties across booking platforms',
    icon: Link2,
    tags: ['channel manager', 'airbnb', 'expedia', 'vrbo', 'sync'],
    content: `Connect to Expedia, Booking.com, VRBO, and Airbnb from your Channel Manager tab. Click "Connect" next to each platform and enter your credentials. Benefits include automatic calendar sync, unified inbox, and centralized pricing.`
  },
  {
    id: 'vr-pricing',
    category: 'vacation-rental',
    title: 'Vacation Rental Pricing',
    description: 'Pricing and fees overview',
    icon: DollarSign,
    tags: ['pricing', 'fees', 'cost'],
    content: `Setup: $99 | Monthly: $19 (1-3 props), $69 (4-10), $199 (11-25), $599 (26-50) | Transaction: 4%. Compare to Airbnb/Expedia at 15-25%!`
  },
  {
    id: 'vr-payouts',
    category: 'vacation-rental',
    title: 'Receiving Payouts',
    description: 'How to receive your earnings',
    icon: CreditCard,
    tags: ['payout', 'payment', 'bank'],
    content: `Go to Payouts tab > Payout Settings. Methods: ACH (3-5 days), Visa Direct (instant), PayPal (1-2 days), Zelle (same day). Minimum $50 to request.`
  },
  {
    id: 'landlord-collect-rent',
    category: 'landlord',
    title: 'Collecting Rent Online',
    description: 'How rent collection works',
    icon: DollarSign,
    tags: ['rent', 'payment', 'collection'],
    content: `Tenants set up payment method, system sends reminders, tenant pays via dashboard, funds deposited to your bank in 2-3 days. Configure late fees in Settings.`
  },
  {
    id: 'tenant-maintenance',
    category: 'tenant',
    title: 'Submitting Maintenance Requests',
    description: 'How to report issues',
    icon: Wrench,
    tags: ['maintenance', 'repair', 'request'],
    content: `Login > Maintenance > New Request. Select category, describe issue, upload photos, set urgency, submit. Track status in your dashboard.`
  },
  {
    id: 'hoa-admin-getting-started',
    category: 'hoa',
    title: 'HOA Administrator Guide',
    description: 'Setting up HOA management',
    icon: Building2,
    tags: ['hoa', 'administrator', 'community'],
    content: `Sign up at /signup > HOA Administrator. Login at /hoa-admin/login. Features: Units, Owners, Dues Collection, Assessments, Communications.`
  },
  {
    id: 'hoa-owner-pay-dues',
    category: 'hoa-owner',
    title: 'Paying HOA Dues',
    description: 'How to pay your HOA dues',
    icon: CreditCard,
    tags: ['hoa', 'dues', 'payment'],
    content: `Login at /hoa-owner/login > Pay Dues. Add payment method if needed, confirm amount, submit. Enable Auto-Pay to never miss a payment.`
  },
  {
    id: 'referral-program',
    category: 'referral',
    title: 'Referral Program',
    description: 'Earn 25% recurring commission',
    icon: DollarSign,
    tags: ['referral', 'commission', 'earn'],
    content: `Login at /referral/login. Get your unique link, share it, earn 25% of every referral's subscription forever. Payouts via ACH, PayPal, Zelle, or Visa Direct.`
  },
  {
    id: 'contact-support',
    category: 'account',
    title: 'Contact Support',
    description: 'Get help from our team',
    icon: MessageCircle,
    tags: ['support', 'help', 'contact'],
    content: `Chat with AI assistant (click chat bubble), browse Knowledge Base at /kb, or email support@apcestateflow.com. We respond within 24 hours.`
  }
];

const ALL_ARTICLES = [...ARTICLES, ...TEXT_ARTICLES];

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Topics', icon: BookOpen },
    { id: 'vacation-rental', label: 'Vacation Rentals', icon: Palmtree },
    { id: 'landlord', label: 'Landlords', icon: Building2 },
    { id: 'tenant', label: 'Tenants', icon: UserCheck },
    { id: 'property-manager', label: 'Property Managers', icon: ClipboardList },
    { id: 'hoa', label: 'HOA Admin', icon: Users },
    { id: 'hoa-owner', label: 'HOA Owners', icon: Home },
    { id: 'referral', label: 'Referral Partners', icon: DollarSign },
    { id: 'account', label: 'Account & Login', icon: Key }
  ];

  const filteredArticles = useMemo(() => {
    return ALL_ARTICLES.filter(article => {
      const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
      const matchesSearch = searchQuery === '' || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  // Article detail view
  if (selectedArticle) {
    const article = ALL_ARTICLES.find(a => a.id === selectedArticle);
    if (!article) return null;
    
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setSelectedArticle(null)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Knowledge Base
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                {article.icon && <article.icon className="h-8 w-8 text-emerald-600" />}
                <Badge variant="outline" className="capitalize">{article.category.replace('-', ' ')}</Badge>
                {article.hasVisuals && <Badge className="bg-blue-100 text-blue-800">Visual Guide</Badge>}
              </div>
              <CardTitle className="text-2xl">{article.title}</CardTitle>
              <CardDescription>{article.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {article.renderContent ? (
                article.renderContent()
              ) : (
                <p className="text-gray-700 whitespace-pre-wrap">{article.content}</p>
              )}
              
              <div className="mt-8 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Related topics:</p>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <Badge 
                      key={tag} 
                      variant="secondary" 
                      className="cursor-pointer hover:bg-emerald-100"
                      onClick={() => {
                        setSelectedArticle(null);
                        setSearchQuery(tag);
                      }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 bg-emerald-50 border-emerald-200">
            <CardContent className="py-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="font-semibold text-emerald-900">Still need help?</h3>
                  <p className="text-sm text-emerald-700">Our support team is ready to assist.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate('/')}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Chat with AI
                  </Button>
                  <a href="mailto:support@apcestateflow.com">
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <Mail className="h-4 w-4 mr-2" />
                      Email Support
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Main view
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <BookOpen className="h-8 w-8" />
              <span className="text-xl font-bold">Estate Flow Help Center</span>
            </div>
            <Button variant="outline" className="text-white border-white hover:bg-white/10" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-4">How can we help you?</h1>
          <p className="text-emerald-100 text-center mb-8">Search our knowledge base or browse by category</p>
          
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help... (e.g., 'add property', 'pay rent')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg text-gray-900"
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8 overflow-x-auto">
          <div className="flex flex-wrap justify-center gap-2 min-w-max">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.id 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <cat.icon className="h-4 w-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-gray-500 mb-4">{filteredArticles.length} articles found</p>

        {filteredArticles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map(article => (
              <Card 
                key={article.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => setSelectedArticle(article.id)}
              >
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200">
                      <article.icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    {article.hasVisuals && <Badge className="bg-blue-100 text-blue-800 text-xs">Visual</Badge>}
                  </div>
                  <CardTitle className="text-lg group-hover:text-emerald-600 flex items-center justify-between">
                    <span className="line-clamp-2">{article.title}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-emerald-600 flex-shrink-0" />
                  </CardTitle>
                  <CardDescription className="line-clamp-2">{article.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <HelpCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No articles found</h3>
            <Button onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>View All</Button>
          </div>
        )}

        <Card className="mt-12 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="py-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Can't find what you need?</h2>
            <p className="text-gray-600 mb-6">Our support team is here to help.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:support@apcestateflow.com">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Mail className="h-4 w-4 mr-2" />
                  support@apcestateflow.com
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </main>

      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400">© 2026 Estate Flow by Sebastian Alliance Group LLC</p>
        </div>
      </footer>
    </div>
  );
};

export default KnowledgeBase;
